import moment from 'moment-timezone';
import { ComponentProps, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { DayPicker, TZDate } from 'react-day-picker';
import 'react-day-picker/style.css';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateRangePickerLayout } from '../shared/DateRangePickerLayout';
import { getDayPickerLocale } from '../shared/day-picker-locale';
import { useSharedDayPickerStyles } from '../shared/useSharedDayPickerStyles';
import type { DateRangeValue } from './date-range-presets';

export interface CustomFixedRangePickerProps {
  open: boolean;
  onClose: () => void;
  onApply: (range: DateRangeValue) => void;
  /** Initial range for the picker. */
  initialRange?: DateRangeValue | null;
  /**
   * Earliest selectable instant; DayPicker disables days strictly before this boundary (absolute `Date`, not re-timezoned).
   */
  minSelectableDate?: Date;
  /**
   * Latest selectable instant; DayPicker disables days strictly after this boundary (absolute `Date`).
   */
  maxSelectableDate?: Date;
  /** IANA time zone for calendar days and start/end-of-day when applying. */
  tz?: string;
}

function normalizeRange(from: Date, to: Date): DateRangeValue {
  return from.getTime() <= to.getTime() ? { from, to } : { from: to, to: from };
}

/** Converts a Date to a TZDate in the given zone when tz is set. */
function toPickerDate(date: Date, tz: string | undefined): Date {
  return tz ? new TZDate(date, tz) : new Date(date);
}

/**
 * Builds an inclusive full-day range in the station timezone (start of from → end of to).
 * Falls back to local midnight/end-of-day when tz is omitted.
 */
export function toStationDayRange(from: Date, to: Date, tz?: string): DateRangeValue {
  if (tz) {
    const fromDate = moment(from).tz(tz).startOf('day').toDate();
    const toDate = moment(to).tz(tz).endOf('day').toDate();
    return normalizeRange(fromDate, toDate);
  }

  const fromDate = new Date(from);
  fromDate.setHours(0, 0, 0, 0);
  const toDate = new Date(to);
  toDate.setHours(23, 59, 59, 999);
  return normalizeRange(fromDate, toDate);
}

/**
 * Inline range date picker for "Custom fixed date range".
 * Renders below the selector (inside Menu/Popover). Two months, no time selection.
 * When tz is set, calendar selection and apply use that timezone (not the browser).
 */
export function CustomFixedRangePicker({
  open,
  onClose,
  onApply,
  initialRange,
  minSelectableDate,
  maxSelectableDate,
  tz,
}: CustomFixedRangePickerProps) {
  const { locale } = useIntl();
  const dayPickerStyles = useSharedDayPickerStyles();
  const [selected, setSelected] = useState<DateRange | undefined>(() => {
    if (initialRange?.from && initialRange?.to) {
      return {
        from: toPickerDate(initialRange.from, tz),
        to: toPickerDate(initialRange.to, tz),
      };
    }
    return undefined;
  });

  // Sync selection when range view opens; intentionally not when initialRange changes so we don't overwrite user selection.
  useEffect(() => {
    if (!open) return;
    if (initialRange?.from && initialRange?.to) {
      setSelected({
        from: toPickerDate(initialRange.from, tz),
        to: toPickerDate(initialRange.to, tz),
      });
    } else {
      setSelected(undefined);
    }
  }, [open, tz]);

  const disabled = useMemo((): ComponentProps<typeof DayPicker>['disabled'] => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (minSelectableDate) {
      matchers.push({ before: minSelectableDate });
    }
    if (maxSelectableDate) {
      matchers.push({ after: maxSelectableDate });
    }
    if (matchers.length === 0) {
      return undefined;
    }
    return matchers.length === 1 ? matchers[0] : matchers;
  }, [minSelectableDate, maxSelectableDate]);

  const handleApply = () => {
    if (!selected?.from) return;
    const to = selected.to ?? selected.from;
    onApply(toStationDayRange(selected.from, to, tz));
    onClose();
  };

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
  };
  const fromLabel = selected?.from ? selected.from.toLocaleDateString(locale, dateOptions) : '—';
  const toLabel = selected?.to ? selected.to.toLocaleDateString(locale, dateOptions) : '—';
  const defaultMonth = selected?.from ?? (tz ? TZDate.tz(tz) : new Date());

  return (
    <DateRangePickerLayout
      title={
        <FormattedMessage
          id="common.dateRangeSelector.selectDateRangeTitle"
          defaultMessage="Seleccionar rango de fechas"
        />
      }
      summary={
        <FormattedMessage
          id="common.dateRangeSelector.selectedLabel"
          defaultMessage="Seleccionado: {from} – {to}"
          values={{ from: fromLabel, to: toLabel }}
        />
      }
      onCancel={onClose}
      onApply={handleApply}
      applyDisabled={!selected?.from}
    >
      <DayPicker
        mode="range"
        selected={selected}
        onSelect={setSelected}
        numberOfMonths={2}
        defaultMonth={defaultMonth}
        locale={getDayPickerLocale(locale)}
        styles={dayPickerStyles}
        disabled={disabled}
        timeZone={tz}
      />
    </DateRangePickerLayout>
  );
}
